import { useIntl } from 'react-intl';
import { useMemo } from 'react';
import { APPLICATION_STATES } from './constants';
import type { ApplicationState } from '../../redux/features/jobs/jobsSlice';
import type { ApplicationStateButtonState } from './types';
import type { IconName } from '../icons/iconPaths';

/** Result for static render: color + icon only (no intl label). */
export interface ApplicationStateButtonStateStatic {
  color: 'purple' | 'green' | 'red' | 'emerald' | 'blue';
  icon: IconName;
}

/**
 * Pure function: computes button display state for static shell (no Intl).
 * Priority: exclusive state (interviewing, offer, rejected, hired) > generic applied > null.
 */
export function getApplicationStateButtonStateForStatic(
  currentApplicationState: ApplicationState | string | null | undefined,
  isApplied: boolean
): ApplicationStateButtonStateStatic | null {
  if (currentApplicationState) {
    const stateConfig = APPLICATION_STATES.find((s) => s.value === currentApplicationState);
    if (stateConfig) {
      return { color: stateConfig.color, icon: stateConfig.iconSolid };
    }
  }
  if (isApplied) {
    return { color: 'blue', icon: 'checkCircle' };
  }
  return null;
}

/**
 * Computes the button state to display based on application state and applied flag
 * Priority: exclusive state > applied > nothing
 */
export function useApplicationStateButtonState(
  currentApplicationState: ApplicationState | null | undefined,
  isApplied: boolean
): ApplicationStateButtonState | null {
  const intl = useIntl();

  return useMemo(() => {
    if (currentApplicationState) {
      const stateConfig = APPLICATION_STATES.find(s => s.value === currentApplicationState);
      if (stateConfig) {
        return {
          label: intl.formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel }),
          icon: stateConfig.iconSolid,
          color: stateConfig.color,
        };
      }
    }
    
    if (isApplied) {
      return {
        label: intl.formatMessage({ id: 'tools.applicationState.applied', defaultMessage: 'Applied' }),
        icon: 'checkCircle',
        color: 'blue',
      };
    }
    
    return null;
  }, [currentApplicationState, isApplied, intl]);
}
